import { MORTGAGE_APP_URL } from 'components/globals';
import { formatCurrency } from 'helpers/formatters';

import type {
  MortgageActiveLoanQuery,
  MortgageLoanStatus,
  MortgageLoanType,
  MortgageTaskStatus,
} from '../mortgage/__generated__/mortgage.zeus';
import type { TransactionCardModel } from './TransactionCard';

/** The subset of the mortgage `Loan` the hub card queries and renders. */
export type MortgageLoan = NonNullable<
  MortgageActiveLoanQuery['customerAccount']['buyer']['mortgage']['activeLoan']
>;
type MortgageTask = MortgageLoan['borrowers'][number]['tasks'][number];

// pendingTaskCount includes internal tasks, so the hub applies the mortgage
// app's borrower-facing visibility rules instead of using that aggregate.
const CANCELED_STATUSES: ReadonlySet<MortgageTaskStatus> = new Set([
  'AUTOMATICALLY_CANCELLED',
  'MANUALLY_CANCELLED',
]);
const ADD_SUBJECT_PROPERTY_PATTERN = /^add subject property$/i;

const isAddSubjectPropertyTask = (task: MortgageTask): boolean =>
  task.taskType === 'INPUT_REQUIRED' && ADD_SUBJECT_PROPERTY_PATTERN.test(task.title.trim());

const isRateLockTask = (task: MortgageTask): boolean => task.taskType === 'RATE_LOCK';

const isVisibleTask = (task: MortgageTask): boolean => {
  if (CANCELED_STATUSES.has(task.status)) {
    return false;
  }
  if (isAddSubjectPropertyTask(task)) {
    return false;
  }

  return (
    task.taskCategory === 'BORROWER_TASK' &&
    (task.taskType === 'DOCUMENT_REQUIRED' ||
      isRateLockTask(task) ||
      task.taskType === 'DISCLOSURE_SIGNING' ||
      task.requiredFor1003Form === true)
  );
};

const countOpenBorrowerTasks = (loan: MortgageLoan): number => {
  const tasks = loan.borrowers.flatMap((borrower) => borrower.tasks);

  return tasks.filter(
    (task) =>
      isVisibleTask(task) && (task.status === 'NOT_STARTED' || task.status === 'IN_PROGRESS'),
  ).length;
};

const TOTAL_STAGES = 6;

const LOAN_STATUS_LABELS: Record<MortgageLoanStatus, string> = {
  NOT_STARTED: 'Application In Progress',
  IN_PROGRESS: 'Application In Progress',
  ACCOUNT_CREATED: 'Account Created',
  MANUAL_REVIEW: 'Manual Review',
  PRE_APPROVED: 'Pre-Approved',
  PROCESSING: 'Processing',
  UNDERWRITING: 'Underwriting Review',
  CONDITIONALLY_APPROVED: 'Conditionally Approved',
  FINAL_REVIEW: 'Final Review',
  PRE_CLOSING_QUALITY_REVIEW: 'Final Review',
  CLEAR_TO_CLOSE: 'Clear to Close',
  DOCS_OUT: 'Docs Out',
  FUNDING: 'Funding',
  CLOSED: 'Closed',
  SHIPPING: 'Shipping',
  POST_CLOSING: 'Post Closing',
  PURCHASED: 'Purchased',
  POST_PURCHASED: 'Post Purchased',
  SERVICING: 'Servicing',
  DECLINED: 'Not Approved',
  SUSPENDED: 'On Hold',
  ARCHIVED: 'Archived',
  WITHDRAWN: 'Withdrawn',
};

const LOAN_STATUS_STAGES: Record<MortgageLoanStatus, number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 0,
  ACCOUNT_CREATED: 0,
  DECLINED: 0,
  SUSPENDED: 0,
  ARCHIVED: 0,
  WITHDRAWN: 0,
  MANUAL_REVIEW: 1,
  PRE_APPROVED: 1,
  PROCESSING: 2,
  UNDERWRITING: 3,
  CONDITIONALLY_APPROVED: 4,
  FINAL_REVIEW: 4,
  PRE_CLOSING_QUALITY_REVIEW: 4,
  CLEAR_TO_CLOSE: 5,
  DOCS_OUT: 5,
  FUNDING: 6,
  CLOSED: 6,
  SHIPPING: 6,
  POST_CLOSING: 6,
  PURCHASED: 6,
  POST_PURCHASED: 6,
  SERVICING: 6,
};

const NEGATIVE_LOAN_STATUSES: ReadonlySet<MortgageLoanStatus> = new Set([
  'DECLINED',
  'SUSPENDED',
  'ARCHIVED',
  'WITHDRAWN',
]);
const COMPLETED_LOAN_STATUSES: ReadonlySet<MortgageLoanStatus> = new Set([
  'CLOSED',
  'SHIPPING',
  'POST_CLOSING',
  'PURCHASED',
  'POST_PURCHASED',
  'SERVICING',
]);
const isCompletedLoan = (loan: MortgageLoan): boolean =>
  loan.loanStatus !== null && COMPLETED_LOAN_STATUSES.has(loan.loanStatus);

// A disqualified application is terminal and overrides the loan status: the
// borrower didn't qualify, so we show that regardless of pipeline position.
const isTerminalNegative = (loan: MortgageLoan): boolean =>
  loan.applicationStatus === 'DISQUALIFIED' ||
  (loan.loanStatus !== null && NEGATIVE_LOAN_STATUSES.has(loan.loanStatus));

const LOAN_TYPE_LABELS: Record<MortgageLoanType, string> = {
  CONVENTIONAL: 'Conventional',
  FHA: 'FHA',
  USDA: 'USDA',
  VA: 'VA',
};

const SINGULAR_TASK_COUNT = 1;

const buildFooterMessage = (loan: MortgageLoan): string | null => {
  if (isCompletedLoan(loan)) {
    return 'Your home loan is complete';
  }
  if (isTerminalNegative(loan)) {
    return 'You can still see your loan details';
  }
  if (loan.applicationStatus === 'IN_PROGRESS') {
    return null;
  }
  const openTaskCount = countOpenBorrowerTasks(loan);
  if (openTaskCount > 0) {
    const suffix = openTaskCount === SINGULAR_TASK_COUNT ? '' : 's';
    return `You have ${openTaskCount} new task${suffix}`;
  }
  return 'Track your home loan progress';
};

// Loan amount is the headline figure once known; before it's set, purchase price
// and then the pre-approval ceiling are the next best things to surface.
const buildPriceLabel = (loan: MortgageLoan): string | null => {
  if (loan.loanAmountCents != null) {
    return `${formatCurrency(loan.loanAmountCents)} loan`;
  }
  if (loan.purchasePriceCents != null) {
    return formatCurrency(loan.purchasePriceCents);
  }
  const maxPurchasePriceCents = loan.latestPreapproval?.maxPurchasePriceCents;
  if (maxPurchasePriceCents != null) {
    return `Approved up to ${formatCurrency(maxPurchasePriceCents)}`;
  }
  return null;
};

// A specs line mirroring the home-purchase card's "3 bed · 3 bath" detail: the
// loan's type, term, and locked/quoted rate, showing only the parts we have.
const buildDetailsLine = (loan: MortgageLoan): string | null => {
  const termYears = loan.loanTermYears ?? loan.selectedPricingRate?.loanTermYears;
  const rate = loan.selectedPricingRate?.rate;
  const parts = [
    loan.loanType ? LOAN_TYPE_LABELS[loan.loanType] : null,
    termYears ? `${termYears}-yr` : null,
    rate != null ? `${rate}% rate` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : null;
};

const buildCtaLabel = (loan: MortgageLoan): string | null => {
  if (isCompletedLoan(loan)) {
    return null;
  }
  if (loan.applicationStatus === 'IN_PROGRESS') {
    return 'Complete your application';
  }
  return 'View details';
};

export const buildMortgageLoanCardModel = (
  loan: MortgageLoan,
  // The purchased home's photo, so the card mirrors the Home purchase card;
  // falls back to a neutral house placeholder when there's no photo.
  propertyPhotoUrl: string | null = null,
): TransactionCardModel => {
  const isDisqualified = loan.applicationStatus === 'DISQUALIFIED';
  const isMuted = isTerminalNegative(loan);
  const isComplete = isCompletedLoan(loan);
  const completedSegments =
    isMuted || loan.loanStatus === null ? 0 : (LOAN_STATUS_STAGES[loan.loanStatus] ?? 0);
  const loanStatusLabel =
    loan.loanStatus === null
      ? 'Application In Progress'
      : (LOAN_STATUS_LABELS[loan.loanStatus] ?? 'Application In Progress');

  return {
    id: loan.id,
    href: `${MORTGAGE_APP_URL}/my-application`,
    media: { kind: 'image', url: propertyPhotoUrl },
    addressLine: 'Opendoor Home Loans',
    priceLabel: buildPriceLabel(loan),
    detailsLine: buildDetailsLine(loan),
    isMuted,
    isComplete,
    totalSegments: TOTAL_STAGES,
    completedSegments,
    statusLabel: isDisqualified ? 'Not Approved' : loanStatusLabel,
    footerMessage: buildFooterMessage(loan),
    ctaLabel: buildCtaLabel(loan),
    ctaAnalyticsName: isComplete ? undefined : 'hub-mortgage-view-details',
    ctaVariant: isMuted ? 'secondary' : 'primary',
  };
};
